import { css } from '@emotion/react';
import ClickableContainer from 'components/clickable-container';
import React, { useState, useRef } from 'react';
import NvIcon from 'shared/components/nv-icon';
import { primary, white } from 'styles/global_defaults/colors';


export type InlineVideoPreviewProps = {
  videoSrc: string;
  thumbnailSrc?: string;
  title: string;
  width?: number;
  height?: number;
  dataQa?: string;
  dataQaId?: string;
  ariaLabel?: string;
};

const styles = (width: number, height: number) => css`
  aspect-ratio: 16/9;

  .thumbnail-wrapper {
    transition: transform 0.2s;
    overflow: hidden;
    width: ${width !== undefined ? `${width}px` : '100%'};
    height: ${height !== undefined ? `${height}px` : '100%'};
  }

  .thumbnail-wrapper:hover {
    transform: scale(1.02);
  }

  .thumbnail-image {
    object-fit: cover;
    border-radius: 8px;
  }

  .play-button-overlay {
    top: 0;
    left: 0;
    opacity: 0.9;
    transition: opacity 0.3s;
  }

  .play-button-overlay svg {
    width: 17%;
    height: auto;
    min-width: 24px;
    min-height: 24px;
  }

  .thumbnail-wrapper:hover .play-button-overlay {
    opacity: 1;
  }

  .play-button-bg {
    fill: rgba(0, 0, 0, 0.7);
    transition: fill 0.3s ease;
  }

  .thumbnail-wrapper:hover .play-button-bg {
    fill: ${primary};
  }

  .inline-video {
    aspect-ratio: 16/9;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .close-btn {
    top: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.6);
    width: 32px;
    height: 32px;
    z-index: 2;

    i {
      color: ${white} !important;
    }
  }
`;

const InlineVideoPreview = ({
  videoSrc,
  thumbnailSrc,
  title,
  width,
  height,
  dataQa,
  dataQaId,
  ariaLabel,
}: InlineVideoPreviewProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handleClose = () => {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  // TODO: Talk to Design about if we actually need to do all of this
  // (As opposed to just using the native video controls, poster attribute, etc.)
  return (
    <div
      css={styles(width, height)}
      className='position-relative'
    >
      {!isPlaying ? (
        <div
          className='thumbnail-wrapper position-relative border-radius-1 cursor-pointer'
          onClick={handlePlay}
          data-qa={dataQa}
          data-qa-id={dataQaId}
          aria-label={ariaLabel}
        >
          {thumbnailSrc ? <img src={thumbnailSrc} alt={title} className='thumbnail-image w-100 h-100' /> : <div>{title}</div>}
          <div className='play-button-overlay position-absolute w-100 h-100 d-flex align-items-center justify-content-center'>
            <svg width='40' height='40' viewBox='0 0 24 24' fill='none'>
              <circle
                cx='12'
                cy='12'
                r='11'
                fill='rgba(0,0,0,0.7)'
                className='play-button-bg'
              />
              <path d='M9 7L17 12L9 17V7Z' fill='white' />
            </svg>
          </div>
        </div>
      ) : (
        <>
          <ClickableContainer
            className='close-btn position-absolute d-flex align-items-center justify-content-center rounded-circle font-size-large cursor-pointer'
            onClick={handleClose}
          >
            <NvIcon icon='close' size='xss-smallest' />
          </ClickableContainer>
          <video
            ref={videoRef}
            src={videoSrc}
            controls
            autoPlay
            className='inline-video w-100'
            title={title}
          />
        </>
      )}
    </div>
  );
};

export default InlineVideoPreview;
